import { useDebounce } from '@uidotdev/usehooks';
import { useState } from 'react';

import useSearchProducts from '@/shared/hooks/useSearchProducts';
import ProductVariant from '@/shared/types/products/productVariant.type';

import useScanningDevice from '../../WaveUIProductsSearchBar/hooks/useScanningDevice';

const escapeApostrophes = (input: string): string => {
  return input.replace(/'/g, "\\'");
};

const useProductsSearchBar = () => {
  const [selectedValue, setSelectedValue] = useState<ProductVariant | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState('');
  const [searchValueReason, setSearchValueReason] = useState('');
  const searchValueDebounced = useDebounce(searchValue, 500);

  const { productsResponse, loading } = useSearchProducts({
    shouldPreventSearch: searchValueReason === 'reset',
    searchFilters: {
      columns: ['product_id'],
      astype: 'unique_list',
      search: [],
      page_size: 40,
      text_search:
        searchValueDebounced === ''
          ? undefined
          : {
              fields: {
                sku: 1,
                product_name: 3,
              },
              text: escapeApostrophes(searchValueDebounced),
            },
    },
  });

  const barcodeScannerProps = useScanningDevice();

  const variants: ProductVariant[] | undefined =
    productsResponse?.data?.flatMap((product) =>
      product.variants.map((variant) => ({
        ...variant,
        value: variant.value || product.value || 0,
        product_prices: variant.product_prices || product.product_prices,
        product_qty: 1,
      })),
    );

  const onSearchValueChange = (value: string, reason: string) => {
    if (reason !== 'reset') {
      setSearchValue(value);
    }
    setSearchValueReason(reason);
  };

  return {
    loading,
    variants: variants || [],
    searchValue,
    barcodeScannerProps,
    selectedValue,
    onSearchValueChange,
    setSelectedValue,
  };
};

export default useProductsSearchBar;
